import _extends from "@babel/runtime/helpers/extends";
import { Alert, Button, Form, Input, Radio, Result, Spin } from "antd";
import { observer } from "mobx-react";
import { useState, useCallback } from "react";
import { useRealCommerceErrorHandling } from "../../hooks";
import { IMPREZA_BUTTON_PROPS, _i, __ } from "../../utils";
import { RealCommerceError } from "../../exceptions";
import { useStores } from "../../store";
import { ESupportTicketSubscriptionCancelationTypeOfCancellation } from "@devowl-wp/api-real-commerce";
import moment from "moment";
import { DatePicker } from "../../components";
const RequestCancellationFormLayout = {
  labelCol: {
    span: 6
  },
  wrapperCol: {
    span: 16
  }
};
const RequestCancellationForm = observer(_ref => {
  let {
    customercenter
  } = _ref;
  const {
    accountStore
  } = useStores();
  const [busy, setBusy] = useState(false);
  const [createdForEmail, setCreatedForEmail] = useState();
  const initialValues = {
    name: "",
    email: "",
    typeOfCancellation: ESupportTicketSubscriptionCancelationTypeOfCancellation.OrdinaryCancellation,
    justificationOfCancellation: "",
    productToBeCancelled: "",
    wishedDateOfTerminationType: "nextPossible",
    wishedDateOfTermination: moment().add(1, "day")
  };
  const [form] = Form.useForm();
  const handleRealCommerceError = useRealCommerceErrorHandling();
  const onFinish = useCallback(async _ref2 => {
    let {
      wishedDateOfTerminationType,
      ...rest
    } = _ref2;
    setBusy(true);
    try {
      var _rest$wishedDateOfTer;
      const data = {
        ...rest,
        wishedDateOfTermination: wishedDateOfTerminationType === "nextPossible" ? moment().add(1, "day").toDate() : (_rest$wishedDateOfTer = rest.wishedDateOfTermination) === null || _rest$wishedDateOfTer === void 0 ? void 0 : _rest$wishedDateOfTer.toDate()
      };
      await accountStore.requestCancellation(data);
      setCreatedForEmail(rest.email);
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
    } finally {
      setBusy(false);
    }
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: "real-commerce-request-cancellation-form"
  }, /*#__PURE__*/React.createElement(Spin, {
    spinning: busy
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 25,
      fontWeight: "bold"
    }
  }, __("Cancellation")), createdForEmail ? /*#__PURE__*/React.createElement(Result, {
    title: __("Cancellation requested!"),
    subTitle: _i(__("If there is a subscription for your email address, a support request has been opened and you will receive an email confirmation within a few seconds. If you do not receive a confirmation, please consider this as a rejection of your request, because we could not find a subscription for your email address.", createdForEmail), {
      strong: /*#__PURE__*/React.createElement("strong", null)
    }),
    status: "success"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, !!customercenter && /*#__PURE__*/React.createElement(Alert, {
    type: "info",
    showIcon: true,
    message: _i(__("The best way to cancel is to login to the {{a}}customer center{{/a}}. If you cancel here, please make sure to fill out all fields, so that we know what exactly you want to cancel!"), {
      a: /*#__PURE__*/React.createElement("a", {
        href: customercenter,
        rel: "noreferrer"
      })
    }),
    style: {
      marginTop: 20
    }
  }), /*#__PURE__*/React.createElement(Form, _extends({
    form: form,
    onFinish: onFinish,
    initialValues: initialValues,
    style: {
      marginTop: 20
    },
    labelWrap: true
  }, RequestCancellationFormLayout), /*#__PURE__*/React.createElement(Form.Item, {
    name: "name",
    label: __("Your full name")
  }, /*#__PURE__*/React.createElement(Input, null)), /*#__PURE__*/React.createElement(Form.Item, {
    name: "email",
    label: __("Email address"),
    rules: [{
      type: "email",
      required: true,
      message: __("Please enter a valid email address!")
    }],
    required: true
  }, /*#__PURE__*/React.createElement(Input, null)), /*#__PURE__*/React.createElement(Form.Item, {
    name: "typeOfCancellation",
    label: __("Type of cancellation"),
    required: true,
    rules: [{
      required: true,
      message: __("Please choose one option!")
    }]
  }, /*#__PURE__*/React.createElement(Radio.Group, null, /*#__PURE__*/React.createElement(Radio.Button, {
    value: ESupportTicketSubscriptionCancelationTypeOfCancellation.OrdinaryCancellation
  }, __("Ordinary cancellation")), /*#__PURE__*/React.createElement(Radio.Button, {
    value: ESupportTicketSubscriptionCancelationTypeOfCancellation.ExtraordinaryCancellation
  }, __("Extraordinary cancellation")))), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.typeOfCancellation !== nextValues.typeOfCancellation
  }, _ref3 => {
    let {
      getFieldValue
    } = _ref3;
    return getFieldValue("typeOfCancellation") === ESupportTicketSubscriptionCancelationTypeOfCancellation.ExtraordinaryCancellation && /*#__PURE__*/React.createElement(Form.Item, {
      name: "justificationOfCancellation",
      label: __("Justification of cancellation")
    }, /*#__PURE__*/React.createElement(Input.TextArea, null));
  }), /*#__PURE__*/React.createElement(Form.Item, {
    name: "productToBeCancelled",
    label: __("Product to be cancelled")
  }, /*#__PURE__*/React.createElement(Input, {
    placeholder: __("e.g. Real Cookie Banner for 3 websites")
  })), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("Wished date of termination"),
    required: true
  }, /*#__PURE__*/React.createElement(Form.Item, {
    name: "wishedDateOfTerminationType",
    noStyle: true,
    rules: [{
      required: true,
      message: __("Please choose one option!")
    }]
  }, /*#__PURE__*/React.createElement(Radio.Group, null, /*#__PURE__*/React.createElement(Radio.Button, {
    value: "nextPossible"
  }, __("Next possible date")), /*#__PURE__*/React.createElement(Radio.Button, {
    value: "date"
  }, __("Select date"))))), /*#__PURE__*/React.createElement(Form.Item, {
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.wishedDateOfTerminationType !== nextValues.wishedDateOfTerminationType
  }, _ref4 => {
    let {
      getFieldValue
    } = _ref4;
    return getFieldValue("wishedDateOfTerminationType") === "date" && /*#__PURE__*/React.createElement(Form.Item, {
      name: "wishedDateOfTermination",
      wrapperCol: {
        offset: RequestCancellationFormLayout.labelCol.span
      },
      rules: [{
        required: true,
        type: "date",
        message: __("Please select a date!")
      }]
    }, /*#__PURE__*/React.createElement(DatePicker, {
      placeholder: __("Select date"),
      showToday: false,
      disabledDate: d => !d || d.isBefore(moment())
    }));
  }), /*#__PURE__*/React.createElement(Form.Item, {
    wrapperCol: {
      offset: RequestCancellationFormLayout.labelCol.span
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({
    type: "primary",
    size: "large",
    htmlType: "submit"
  }, IMPREZA_BUTTON_PROPS), __("Cancel now")))))));
});
export { RequestCancellationForm };